import { apiFetch, buildQuery } from "./client";
import type { Page } from "./admin";
import type { AttributeDefView } from "./catalog";
import type { PositionKind } from "./structure";

/** Sprint 4 contract: docs/api/sprint4-sabloni-prilozi-komentari.md §2; sprint 5c drops `usedByTypeCount`. */

/** Code of a `position_kind` row (admin codebook). */
export type TemplateNodeKind = PositionKind;

export type TemplateNodeView = {
  id: string;
  parentNodeId: string | null;
  name: string;
  kind: TemplateNodeKind;
  sequenceNo: number | null;
  sortOrder: number;
  expectedTypeId: string | null;
  expectedTypeCode: string | null;
  expectedTypeName: string | null;
  referenceValues: Record<string, unknown>;
  interventionNote: string | null;
  drawingCode: string | null;
  children: TemplateNodeView[];
};

export type TemplateNodeRequest = Omit<
  TemplateNodeView,
  "id" | "parentNodeId" | "children" | "expectedTypeCode" | "expectedTypeName"
> & { children?: TemplateNodeRequest[] };

export type TemplateView = {
  id: string;
  name: string;
  description: string | null;
  sourcePositionId: string | null;
  sourcePositionName: string | null;
  nodeCount: number;
  rootNodeCount: number;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Root nodes with nested children; `attributeDefs` (2026-10-20) = definitions per node type id, to show the saved values. */
export type TemplateDetail = TemplateView & { nodes: TemplateNodeView[]; attributeDefs: Record<string, AttributeDefView[]> };

export type TemplateRequest = {
  name: string;
  description?: string | null;
  /** When present on PUT the whole node tree is replaced. */
  nodes?: TemplateNodeRequest[];
};

export type TemplateFromPositionRequest = {
  positionId: string;
  name: string;
  description?: string | null;
  /** Default true; false → the position's children become the root nodes. */
  includeRoot?: boolean;
  /** Positions below positionId left out of the template, each with everything below it. */
  excludeIds?: string[];
  /** 2026-10-20: position id → keys of filled values left out of the template; absent = every value goes in. */
  omitValues?: Record<string, string[]>;
};

export type TemplateListParams = { q?: string; page?: number; size?: number };

export const templatesApi = {
  list: (params: TemplateListParams = {}) =>
    apiFetch<Page<TemplateView>>(`/templates${buildQuery(params)}`),
  get: (id: string) => apiFetch<TemplateDetail>(`/templates/${id}`),
  /** 201; hand-built node tree (not exposed in the UI yet). */
  create: (body: TemplateRequest) =>
    apiFetch<TemplateDetail>("/templates", { method: "POST", body }),
  /** 201; copies the position's sub-tree (no assets, no codes); 400 ROOT_IMMUTABLE for the root. */
  /** `excludeIds` (2026-10-09): positions below the saved one left out, each with everything below it. */
  fromPosition: (body: TemplateFromPositionRequest) =>
    apiFetch<TemplateDetail>("/templates/from-position", { method: "POST", body }),
  /** name/description; `nodes` present → replaces the whole tree. */
  update: (id: string, body: TemplateRequest) =>
    apiFetch<TemplateDetail>(`/templates/${id}`, { method: "PUT", body }),
  /** 204; a template can always be deleted (sprint 5c) — positions made from it stay. */
  /** `reason` is mandatory (2026-09-22). */
  remove: (id: string, reason: string) => apiFetch<void>(`/templates/${id}?reason=${encodeURIComponent(reason)}`, { method: "DELETE" }),
};

/** Total nodes in a nested node list (used for previews when only `nodes` are at hand). */
export function countTemplateNodes(nodes: TemplateNodeView[]): number {
  return nodes.reduce((n, node) => n + 1 + countTemplateNodes(node.children), 0);
}
